import { usePersonnelDetailsContext } from "../PersonnelDetails"
import { Form, Formik, FormikHelpers } from "formik"
import { IHttpErrorResponseModel } from "@common-models/*"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import {
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_PERSONNEL_CONTRATS_OPTIONS_FOR_SELECT2,
     CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_DEFAULT,
     CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_ECOLE,
     CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_TT,
     CONST_PERSONNEL_STATUT_STATUT_NON_SALARIE,
     CONST_PERSONNEL_STATUTS_OPTIONS_FOR_SELECT2,
     CONST_USER_PERMISSIONS_OPTIONS_FOR_SELECT2,
} from "@common-constants/*"
import { IGetPersonnelDetailsResponseModel, ISetPersonnelInfoProRequestModel } from "../../core/_models"
import { setInfoPersoProRequest } from "../../core/_requests"
import { useQueryClient } from "react-query"
import * as Yup from "yup"
import MyAlert from "@common-utils/MyAlert"
import MyInputField from "@common-utils/fields/MyInputField"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"
import {
     CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT,
     CONST_API_UTILS_GET_AGENCES_ENDPOINT,
     CONST_API_UTILS_GET_ENTITES_ENDPOINT,
     CONST_API_UTILS_GET_FOURNISSEURS_ENDPOINT,
     CONST_API_UTILS_GET_FOURNISSEURS_RESP_ENDPOINT,
     CONST_API_UTILS_GET_INDICATIF_TEL_ENDPOINT,
     CONST_API_UTILS_GET_METIERS_PERSONNEL_INTERNE_URL_ENDPOINT,
} from "../../../../endpoints"
import MyInptuMaskField from "@common-utils/fields/MyInputFieldMask"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import React, { Dispatch, SetStateAction } from "react"
import MySelectField from "@common-utils/fields/MySelectField"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import useUser from "../../../../hooks/useUser"

const InfoProEdit = ({ setIsInfoProOnEdition }: IPropsModel) => {
     const queryClient = useQueryClient()
     const { hasFullStaffManagement } = useUser()
     const canEditProtectedFields = hasFullStaffManagement()
     // Affichage lecture seule (label + valeur) des champs pro protégés en accès restreint
     const readOnlyValue = (value?: React.ReactNode) => (
          <div className="col-form-label fw-bolder fs-6 text-gray-800">{value || <span className="text-muted fw-light">Non renseigné</span>}</div>
     )
     const context = usePersonnelDetailsContext()
     const details = context.detailsQuery.data
     const isNonSalarie = details?.personnel?.statut === CONST_PERSONNEL_STATUT_STATUT_NON_SALARIE

     // Contact du fournisseur préchargé (utilisé comme option par défaut du select responsable).
     const [selectedFournisseurId, setSelectedFournisseurId] = React.useState<number | undefined>(details?.personnel?.fournisseur?.id)

     const schema = Yup.object().shape({
          statut: Yup.string().label(`"Statut"`).nullable().required(),
          contrat: Yup.string().label(`"Contrat"`).nullable().required(),
          emailPro: Yup.string().email().label(`"Email"`).nullable().required(),
          telPro: Yup.string().label(`"Tél."`).nullable().required(),
          telProIndicatif: Yup.number().label(`"Indicatif tél."`).nullable().required(),
          poste: Yup.number().label(`"Poste"`).nullable().required(),
          entite: Yup.number().label(`"Entité"`).nullable().required(),
          responsable: Yup.number().label(`"Responsable"`).nullable().required(),
          linkedinUrl: Yup.string().label(`"LinkedIn"`).nullable(),
          // Heures / options CRA : salarié uniquement ; non salarié : taux journalier + fournisseur.
          heuresTravail: isNonSalarie ? Yup.number().label(`"Heures travail"`).nullable() : Yup.number().label(`"Heures travail"`).required().nullable(),
          heuresSupp: isNonSalarie ? Yup.number().label(`"Heures supp."`).nullable() : Yup.number().label(`"Heures supp."`).required().nullable(),
          dateEntree: Yup.string().label(`"Date entrée"`).required().nullable(),
          optionLignesCra: Yup.array().label(`"Lignes CRA"`),
          salaireBrutMensuel: isNonSalarie ? Yup.number().label(`"Salaire brut mensuel"`).nullable() : Yup.number().label(`"Salaire brut mensuel"`).min(0).nullable().required(),
          tauxJournalierHt: isNonSalarie ? Yup.number().label(`"Taux journalier HT"`).min(0).nullable().required() : Yup.number().label(`"Taux journalier HT"`).nullable(),
          fournisseur: isNonSalarie ? Yup.number().label(`"Fournisseur"`).nullable().required() : Yup.number().label(`"Fournisseur"`).nullable(),
          fournisseurResponsable: isNonSalarie ? Yup.number().label(`"Contact fournisseur"`).nullable().required() : Yup.number().label(`"Contact fournisseur"`).nullable(),
     })

     const initialValues: ISetPersonnelInfoProRequestModel = {
          statut: details?.personnel?.statut,
          agence: details?.personnel?.agence?.id,
          contrat: details?.personnel?.contrat,
          permissions: details?.personnel?.user?.permissions,
          emailPro: details?.personnel?.mailProfessionnel,
          telPro: details?.personnel?.telProfessionnel,
          telProIndicatif: details?.personnel?.telProfessionnelIndicatif?.id,
          poste: details?.personnel?.personnelPostes && details?.personnel?.personnelPostes.length > 0 ? details?.personnel?.personnelPostes[0].poste?.id : undefined,
          entite: details?.personnel?.mainEntite?.id,
          responsable: details?.responsable?.id,
          linkedinUrl: details?.personnel?.linkedinUrl,
          heuresTravail: details?.personnel?.heuresTravail,
          heuresSupp: details?.personnel?.heuresTravailSupp,
          dateEntree: details?.personnel?.dateEntree?.format("YYYY-MM-DD"),
          optionLignesCra: details?.personnel?.typesLigneActiviteCraAutorises?.filter(item => item !== CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_DEFAULT),
          salaireBrutMensuel: details?.personnel?.salaireBrutMensuel,
          tauxJournalierHt: details?.personnel?.tauxJournalierHt,
          fournisseur: details?.personnel?.fournisseur?.id,
          fournisseurResponsable: details?.personnel?.fournisseurResponsable?.id,
     }

     function handleSubmit(values: ISetPersonnelInfoProRequestModel, helpers: FormikHelpers<ISetPersonnelInfoProRequestModel>) {
          helpers.setStatus(null)

          // On n'envoie que les champs pertinents selon le statut : le non salarié n'a pas d'heures / options CRA / salaire
          // (l'heure 0 serait rejetée par la contrainte « supérieur à 0 »), le salarié n'a pas de taux journalier / fournisseur.
          const payload: ISetPersonnelInfoProRequestModel = isNonSalarie
               ? { ...values, heuresTravail: undefined, heuresSupp: undefined, optionLignesCra: undefined, salaireBrutMensuel: undefined }
               : { ...values, tauxJournalierHt: undefined, fournisseur: undefined, fournisseurResponsable: undefined }

          setInfoPersoProRequest(details?.personnel.id as number, payload)
               .then(r => {
                    queryClient.setQueryData<IGetPersonnelDetailsResponseModel | undefined>(
                         context.REACT_QUERY_KEY_GET_PERSONNEL_DETAILS,
                         (prev: IGetPersonnelDetailsResponseModel | undefined) => {
                              if (prev) {
                                   prev = {
                                        ...prev,
                                        ...r.data,
                                   }
                              }
                              return prev
                         }
                    )
                    setIsInfoProOnEdition(false)
                    toast.success("Les informations pro. ont bien été mises à jour.")
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
               })
               .finally(() => {
                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
     }

     return (
          <>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => {
                         console.log(helpers.errors)
                         return (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Statut</label>

                                        <div className="col-lg-8">
                                             {/* Le statut d'un non salarié n'est pas modifiable ici. */}
                                             {isNonSalarie ? (
                                                  readOnlyValue(details?.personnel.statutLabel)
                                             ) : (
                                                  <>
                                                       <MySelectField
                                                            name={"statut"}
                                                            value={helpers.values.statut}
                                                            options={CONST_PERSONNEL_STATUTS_OPTIONS_FOR_SELECT2}
                                                            isInvalid={!!helpers.errors.statut}
                                                            onChange={val => helpers.setFieldValue("statut", val)}
                                                       />
                                                       {helpers.errors.statut && <div className={"text-danger"}>{helpers.errors.statut}</div>}
                                                  </>
                                             )}
                                        </div>
                                   </div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Contrat</label>

                                        <div className="col-lg-8">
                                             {/* Le contrat d'un non salarié n'est pas modifiable ici. */}
                                             {isNonSalarie ? (
                                                  readOnlyValue(details?.personnel.contratLabel)
                                             ) : (
                                                  <>
                                                       <MySelectField
                                                            name={"contrat"}
                                                            value={helpers.values.contrat}
                                                            options={CONST_PERSONNEL_CONTRATS_OPTIONS_FOR_SELECT2}
                                                            isInvalid={!!helpers.errors.contrat}
                                                            onChange={val => helpers.setFieldValue("contrat", val)}
                                                       />
                                                       {helpers.errors.contrat && <div className={"text-danger"}>{helpers.errors.contrat}</div>}
                                                  </>
                                             )}
                                        </div>
                                   </div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Email pro.</label>

                                        <div className="col-lg-8">
                                             {canEditProtectedFields ? (
                                                  <>
                                                       <MyInputField
                                                            name={"emailPro"}
                                                            value={helpers.values.emailPro}
                                                            isInvalid={!!helpers.errors.emailPro}
                                                            onChange={val => helpers.setFieldValue("emailPro", val)}
                                                            placeholder={"Email"}
                                                       />
                                                       {helpers.errors.emailPro && <div className={"text-danger"}>{helpers.errors.emailPro}</div>}
                                                  </>
                                             ) : (
                                                  readOnlyValue(details?.personnel.mailProfessionnel)
                                             )}
                                        </div>
                                   </div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Téléphone</label>

                                        <div className="col-lg-8">
                                             <div className="row">
                                                  <div className="col-lg-6 mb-2 mb-lg-0">
                                                       <MySelectGetAllDataFromServer
                                                            name={"telProIndicatif"}
                                                            value={helpers.values.telProIndicatif}
                                                            url={CONST_API_UTILS_GET_INDICATIF_TEL_ENDPOINT}
                                                            defaultOptions={
                                                                 details?.personnel?.telProfessionnelIndicatif
                                                                      ? [
                                                                             {
                                                                                  value: details?.personnel.telProfessionnelIndicatif.id as number,
                                                                                  label: details?.personnel.telProfessionnelIndicatif.phoneCodeWithPlusPrefixAndCountry as string,
                                                                             },
                                                                        ]
                                                                      : []
                                                            }
                                                            placeholder={"Choisissez un indicatif"}
                                                            isInvalid={!!helpers.errors.telProIndicatif}
                                                            onChange={val => helpers.setFieldValue("telProIndicatif", val)}
                                                       />
                                                       {helpers.errors.telProIndicatif && <div className={"text-danger"}>{helpers.errors.telProIndicatif}</div>}
                                                  </div>
                                                  <div className="col-lg-6">
                                                       <MyInptuMaskField
                                                            name={"telPro"}
                                                            mask={"99 9999999999999999999999999"}
                                                            value={helpers.values.telPro}
                                                            isInvalid={!!helpers.errors.telPro}
                                                            placeholder={"Téléphone"}
                                                            onChange={e => helpers.setFieldValue("telPro", e.target.value)}
                                                       />
                                                       {helpers.errors.telPro && <div className={"text-danger"}>{helpers.errors.telPro}</div>}
                                                  </div>
                                             </div>
                                        </div>
                                   </div>

                                   {/* Métier */}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Métier</label>

                                        <div className="col-lg-8">
                                             {canEditProtectedFields ? (
                                                  <>
                                                       <MySelectGetAllDataFromServer
                                                            name={"poste"}
                                                            value={helpers.values.poste}
                                                            defaultOptions={
                                                                 details?.personnel.personnelPostes && details?.personnel.personnelPostes.length > 0
                                                                      ? [
                                                                             {
                                                                                  value: details?.personnel.personnelPostes[0].poste?.id as number,
                                                                                  label: details?.personnel.personnelPostes[0].poste?.nom as string,
                                                                             },
                                                                        ]
                                                                      : []
                                                            }
                                                            isInvalid={!!helpers.errors.poste}
                                                            onChange={val => helpers.setFieldValue("poste", val)}
                                                            method={"GET"}
                                                            url={CONST_API_UTILS_GET_METIERS_PERSONNEL_INTERNE_URL_ENDPOINT}
                                                       />
                                                       {helpers.errors.poste && <div className={"text-danger"}>{helpers.errors.poste}</div>}
                                                  </>
                                             ) : (
                                                  readOnlyValue(details?.personnel.personnelPostes?.[0]?.poste?.nom)
                                             )}
                                        </div>
                                   </div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Agence</label>

                                        <div className="col-lg-8">
                                             <MySelectGetAllDataFromServer
                                                  name={"agence"}
                                                  value={helpers.values.agence}
                                                  defaultOptions={
                                                       details?.personnel?.agence
                                                            ? [{ value: details?.personnel?.agence.id as number, label: details?.personnel?.agence.nom as string }]
                                                            : []
                                                  }
                                                  isInvalid={!!helpers.errors.agence}
                                                  onChange={val => helpers.setFieldValue("agence", val)}
                                                  url={CONST_API_UTILS_GET_AGENCES_ENDPOINT}
                                             />
                                             {helpers.errors.agence && <div className={"text-danger"}>{helpers.errors.agence}</div>}
                                        </div>
                                   </div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Entité</label>

                                        <div className="col-lg-8">
                                             <MySelectGetAllDataFromServer
                                                  name={"entite"}
                                                  value={helpers.values.entite}
                                                  defaultOptions={
                                                       details?.personnel?.mainEntite
                                                            ? [{ value: details?.personnel?.mainEntite.id as number, label: details?.personnel?.mainEntite.nom as string }]
                                                            : []
                                                  }
                                                  isInvalid={!!helpers.errors.entite}
                                                  onChange={val => helpers.setFieldValue("entite", val)}
                                                  url={CONST_API_UTILS_GET_ENTITES_ENDPOINT}
                                             />
                                             {helpers.errors.entite && <div className={"text-danger"}>{helpers.errors.entite}</div>}
                                        </div>
                                   </div>

                                   {/*Date entrée*/}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">
                                             <span>Date entrée</span>
                                        </label>

                                        <div className="col-lg-8">
                                             {canEditProtectedFields ? (
                                                  <>
                                                       <MyDateDayMonthYearField
                                                            name={"dateEntree"}
                                                            onChange={date => helpers.setFieldValue("dateEntree", date)}
                                                            value={helpers.values.dateEntree}
                                                            isInvalid={!!helpers.errors.dateEntree}
                                                            placeholder={"Date d'entrée"}
                                                       />
                                                       {helpers.errors.dateEntree && <div className={"text-danger"}>{helpers.errors.dateEntree}</div>}
                                                  </>
                                             ) : (
                                                  readOnlyValue(details?.personnel.dateEntree?.format("DD/MM/YYYY"))
                                             )}
                                        </div>
                                   </div>

                                   {/* Rémunération — non salarié : taux journalier HT ; salarié : salaire brut + heures */}
                                   {isNonSalarie ? (
                                        <div className="row mb-4">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6 required">
                                                  <span>Taux journalier HT (€)</span>
                                             </label>

                                             <div className="col-lg-8">
                                                  <MyInputField
                                                       name={"tauxJournalierHt"}
                                                       onChange={val => helpers.setFieldValue("tauxJournalierHt", val)}
                                                       value={helpers.values.tauxJournalierHt}
                                                       isInvalid={!!helpers.errors.tauxJournalierHt}
                                                       placeholder={"Taux journalier HT"}
                                                       type={"number"}
                                                       inputMode={"numeric"}
                                                  />
                                                  {helpers.errors.tauxJournalierHt && <div className={"text-danger"}>{helpers.errors.tauxJournalierHt}</div>}
                                             </div>
                                        </div>
                                   ) : (
                                        <>
                                             {/* Salaire brut mensuel */}
                                             <div className="row mb-4">
                                                  <label className="col-lg-4 col-form-label fw-bold fs-6 required">
                                                       <span>Salaire brut mensuel (€)</span>
                                                  </label>

                                                  <div className="col-lg-8">
                                                       <MyInputField
                                                            name={"salaireBrutMensuel"}
                                                            onChange={val => helpers.setFieldValue("salaireBrutMensuel", val)}
                                                            value={helpers.values.salaireBrutMensuel}
                                                            isInvalid={!!helpers.errors.salaireBrutMensuel}
                                                            placeholder={"Salaire brut mensuel"}
                                                            type={"number"}
                                                            inputMode={"numeric"}
                                                       />
                                                       {helpers.errors.salaireBrutMensuel && <div className={"text-danger"}>{helpers.errors.salaireBrutMensuel}</div>}
                                                  </div>
                                             </div>

                                             {/* Heures travail */}
                                             <div className="row mb-4">
                                                  <label className="col-lg-4 col-form-label fw-bold fs-6 required">
                                                       <span>Heures travail / jour</span>
                                                  </label>

                                                  <div className="col-lg-8">
                                                       <MyInputField
                                                            name={"heuresTravail"}
                                                            onChange={date => helpers.setFieldValue("heuresTravail", date)}
                                                            value={helpers.values.heuresTravail}
                                                            isInvalid={!!helpers.errors.heuresTravail}
                                                            placeholder={"Heures travail"}
                                                            type={"number"}
                                                            inputMode={"numeric"}
                                                       />
                                                       {helpers.errors.heuresTravail && <div className={"text-danger"}>{helpers.errors.heuresTravail}</div>}
                                                  </div>
                                             </div>

                                             {/* Heures supp. */}
                                             <div className="row mb-4">
                                                  <label className="col-lg-4 col-form-label fw-bold fs-6 required">
                                                       <span>Heures supp. / jour</span>
                                                  </label>

                                                  <div className="col-lg-8">
                                                       <MyInputField
                                                            name={"heuresSupp"}
                                                            onChange={date => helpers.setFieldValue("heuresSupp", date)}
                                                            value={helpers.values.heuresSupp}
                                                            isInvalid={!!helpers.errors.heuresSupp}
                                                            placeholder={"Heures supp."}
                                                            type={"number"}
                                                            inputMode={"numeric"}
                                                       />
                                                       {helpers.errors.heuresSupp && <div className={"text-danger"}>{helpers.errors.heuresSupp}</div>}
                                                  </div>
                                             </div>
                                        </>
                                   )}

                                   {details?.responsable && (
                                        <div className="row mb-4">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6 required">Responsable direct</label>

                                             <div className="col-lg-8">
                                                  {canEditProtectedFields ? (
                                                       <>
                                                            <MySelectGetAllDataFromServer
                                                                 name={"responsable"}
                                                                 value={helpers.values.responsable}
                                                                 defaultOptions={
                                                                      details?.responsable
                                                                           ? [{ value: details?.responsable?.id as number, label: details?.responsable.prenomNom as string }]
                                                                           : []
                                                                 }
                                                                 method={"GET"}
                                                                 payload={{ except_ids: [details?.personnel.id as number] }}
                                                                 isInvalid={!!helpers.errors.responsable}
                                                                 onChange={val => helpers.setFieldValue("responsable", val)}
                                                                 url={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                                            />
                                                            {helpers.errors.responsable && <div className={"text-danger"}>{helpers.errors.responsable}</div>}
                                                       </>
                                                  ) : (
                                                       readOnlyValue(details?.responsable?.prenomNom)
                                                  )}
                                             </div>
                                        </div>
                                   )}

                                   <div className="separator separator-dashed my-4"></div>

                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6">LinkedIn URL</label>

                                        <div className="col-lg-8">
                                             {canEditProtectedFields ? (
                                                  <>
                                                       <MyInputField
                                                            name={"linkedinUrl"}
                                                            value={helpers.values.linkedinUrl}
                                                            isInvalid={!!helpers.errors.linkedinUrl}
                                                            onChange={val => helpers.setFieldValue("linkedinUrl", val)}
                                                            placeholder={"LinkedIn"}
                                                       />
                                                       {helpers.errors.linkedinUrl && <div className={"text-danger"}>{helpers.errors.linkedinUrl}</div>}
                                                  </>
                                             ) : (
                                                  readOnlyValue(details?.personnel.linkedinUrl)
                                             )}
                                        </div>
                                   </div>

                                   {/* Fournisseur & contact — non salarié uniquement, éditables */}
                                   {isNonSalarie && (
                                        <>
                                             <div className="separator separator-dashed my-4"></div>
                                             <h3 className={"mb-4 fs-5"}>Fournisseur</h3>

                                             <div className="row mb-4">
                                                  <label className="col-lg-4 col-form-label fw-bold fs-6 required">Fournisseur</label>
                                                  <div className="col-lg-8">
                                                       <MySelectGetAllDataFromServer
                                                            name={"fournisseur"}
                                                            value={helpers.values.fournisseur}
                                                            method={"GET"}
                                                            url={CONST_API_UTILS_GET_FOURNISSEURS_ENDPOINT}
                                                            defaultOptions={
                                                                 details?.personnel?.fournisseur
                                                                      ? [{ value: details?.personnel.fournisseur.id as number, label: details?.personnel.fournisseur.raisonSociale as string }]
                                                                      : []
                                                            }
                                                            isInvalid={!!helpers.errors.fournisseur}
                                                            onChange={val => {
                                                                 // Changer de fournisseur invalide le contact précédent.
                                                                 helpers.setFieldValue("fournisseur", val)
                                                                 helpers.setFieldValue("fournisseurResponsable", undefined)
                                                                 setSelectedFournisseurId(val as number)
                                                            }}
                                                            placeholder={"Rechercher un fournisseur"}
                                                       />
                                                       {helpers.errors.fournisseur && <div className={"text-danger"}>{helpers.errors.fournisseur}</div>}
                                                  </div>
                                             </div>

                                             {helpers.values.fournisseur && (
                                                  <div className="row mb-4">
                                                       <label className="col-lg-4 col-form-label fw-bold fs-6 required">Contact fournisseur</label>
                                                       <div className="col-lg-8">
                                                            <MySelectGetAllDataFromServer
                                                                 // remonte le select quand le fournisseur change, pour recharger les contacts
                                                                 key={`resp-${selectedFournisseurId}`}
                                                                 name={"fournisseurResponsable"}
                                                                 value={helpers.values.fournisseurResponsable}
                                                                 method={"GET"}
                                                                 url={CONST_API_UTILS_GET_FOURNISSEURS_RESP_ENDPOINT}
                                                                 payload={{ fournisseur: helpers.values.fournisseur }}
                                                                 defaultOptions={
                                                                      details?.personnel?.fournisseurResponsable && details?.personnel?.fournisseur?.id === helpers.values.fournisseur
                                                                           ? [
                                                                                  {
                                                                                       value: details?.personnel.fournisseurResponsable.id as number,
                                                                                       label: details?.personnel.fournisseurResponsable.prenomNom as string,
                                                                                  },
                                                                             ]
                                                                           : []
                                                                 }
                                                                 isInvalid={!!helpers.errors.fournisseurResponsable}
                                                                 onChange={val => helpers.setFieldValue("fournisseurResponsable", val)}
                                                                 placeholder={"Contact du fournisseur"}
                                                            />
                                                            {helpers.errors.fournisseurResponsable && <div className={"text-danger"}>{helpers.errors.fournisseurResponsable}</div>}
                                                       </div>
                                                  </div>
                                             )}
                                        </>
                                   )}

                                   {/* Options CRA — masqué en accès restreint et pour le non salarié (concept salarié) */}
                                   {canEditProtectedFields && !isNonSalarie && (
                                        <div className="row mb-4">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6">Options lignes CRA</label>

                                             <div className="col-lg-8">
                                                  <MySelectField
                                                       name={"optionLignesCra"}
                                                       options={[
                                                            {
                                                                 label: "TT",
                                                                 value: CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_TT,
                                                            },
                                                            {
                                                                 label: "École",
                                                                 value: CONST_PERSONNEL_CRA_DETAILS_HEURE_TYPE_ECOLE,
                                                            },
                                                       ]}
                                                       value={helpers.values.optionLignesCra}
                                                       isInvalid={!!helpers.errors.optionLignesCra}
                                                       onChange={val => helpers.setFieldValue("optionLignesCra", val)}
                                                       isMulti
                                                       isSearchable
                                                       isClearable
                                                  />
                                                  {helpers.errors.optionLignesCra && <div className={"text-danger"}>{helpers.errors.optionLignesCra}</div>}
                                             </div>
                                        </div>
                                   )}

                                   {/* Permissions — masquées en accès restreint */}
                                   {hasFullStaffManagement() && (
                                        <>
                                             <div className="separator separator-dashed my-4"></div>

                                             <MySelectFormField
                                                  formikProps={helpers}
                                                  options={CONST_USER_PERMISSIONS_OPTIONS_FOR_SELECT2}
                                                  name={`permissions`}
                                                  isMulti
                                                  label={"Permissions"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                                  placeholder={"Choisissez"}
                                                  classNameLayout={"mb-5"}
                                             />
                                        </>
                                   )}

                                   <div className="separator bg-primary my-5"></div>

                                   {/* Submit button */}
                                   <div className={"text-end"}>
                                        <button
                                             className={"btn btn-sm btn-outline btn-active-secondary d-inline-flex align-items-center me-2"}
                                             type={"button"}
                                             onClick={() => setIsInfoProOnEdition(false)}
                                             disabled={helpers.isSubmitting}
                                        >
                                             Annuler
                                        </button>
                                        <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? <>Appliquer les modifications</> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )
                    }}
               </Formik>
          </>
     )
}

interface IPropsModel {
     setIsInfoProOnEdition: Dispatch<SetStateAction<boolean>>
}

export default InfoProEdit
